import React, { createContext, FC, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import useDebounce from '../../../hooks/useDebouncedFn/useDebouncedFn'
import {
  useGetMaintenanceSchedulesForVehicleQuery,
  useLazyGetMaintenanceScheduleTimelineQuery
} from '../../../redux/api/taxonomyApi'
import {
  GetMaintenanceScheduleTimelineIntervalTypeEnum,
  LaborApplication,
  MaintenanceSchedule,
  MaintenanceScheduleTimingInfo
} from '../../../sdk'
import { MaintenanceScheduleTimeline } from '../../../sdk/models/MaintenanceScheduleTimeline'
export enum SeverityType {
  Normal = 'Normal',
  Severe = 'Severe'
}
export function getIdForMaintenanceSchedule(
  laborApplication: LaborApplication | undefined,
  timing: MaintenanceScheduleTimingInfo
) {
  return (
    (laborApplication?.taxonomy?.literalName || '') +
    (laborApplication?.positionName || '') +
    (timing.frequencyDescription || '') +
    (timing.intervalMile || '') +
    (timing.intervalMonth || '') +
    (timing.indicator || '') +
    (timing.severeServiceDescription || '')
  )
}

interface MaintenanceScheduleContextValues {
  maintenanceSchedules: MaintenanceSchedule[] | undefined
  selected: LaborApplication[]
  setSelected: (s: LaborApplication[]) => void
  maintenanceTimeline:
    | {
        timeline: MaintenanceScheduleTimeline
        intervalType: GetMaintenanceScheduleTimelineIntervalTypeEnum
      }
    | undefined
  intervalType: string
  setIntervalType: React.Dispatch<React.SetStateAction<string>>
  interval: number | undefined
  setInterval: React.Dispatch<React.SetStateAction<number | undefined>>
  severityType: string
  setSeverityType: React.Dispatch<React.SetStateAction<string>>
  loadingSchedule: boolean
  loadingTimeline: boolean
  setMaintenanceTimeline: React.Dispatch<
    React.SetStateAction<
      | {
          timeline: MaintenanceScheduleTimeline
          intervalType: GetMaintenanceScheduleTimelineIntervalTypeEnum
        }
      | undefined
    >
  >
}

export const MaintenanceScheduleContext =
  createContext<MaintenanceScheduleContextValues>({
    maintenanceSchedules: undefined,
    selected: [],
    setSelected: () => {},
    maintenanceTimeline: undefined,
    intervalType: GetMaintenanceScheduleTimelineIntervalTypeEnum.Miles,
    setIntervalType: () => {},
    interval: undefined,
    setInterval: () => {},
    severityType: SeverityType.Normal,
    setSeverityType: () => {},
    loadingSchedule: false,
    loadingTimeline: false,
    setMaintenanceTimeline: () => {}
  })

interface Props {
  vehicleId: string | undefined
  defaultMiles: number | undefined
  children?: React.ReactNode
}

export const MaintenanceScheduleProvider: FC<Props> = ({
  vehicleId,
  defaultMiles,
  children
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [selected, setSelected] = useState<LaborApplication[]>([])
  const [intervalType, setIntervalType] = useState<string>(
    GetMaintenanceScheduleTimelineIntervalTypeEnum.Miles
  )
  const [interval, setInterval] = useState<number | undefined>()
  const debouncedInterval = useDebounce(interval)
  const [severityType, setSeverityType] = useState<string>(SeverityType.Normal)
  useEffect(() => {
    if (defaultMiles !== undefined) {
      setInterval(defaultMiles)
      setIntervalType(GetMaintenanceScheduleTimelineIntervalTypeEnum.Miles)
    }
  }, [defaultMiles])
  const { data: maintenanceSchedules, isLoading: loadingSchedule } =
    useGetMaintenanceSchedulesForVehicleQuery(
      {
        authorization: accessToken,
        shop: shopId,
        vehicle: vehicleId!
      },
      { skip: !vehicleId }
    )

  const [maintenanceTimeline, setMaintenanceTimeline] = useState<
    | {
        timeline: MaintenanceScheduleTimeline
        intervalType: GetMaintenanceScheduleTimelineIntervalTypeEnum
      }
    | undefined
  >(undefined)
  const [getMaintenanceScheduleTimeline, { isFetching: loadingTimeline }] =
    useLazyGetMaintenanceScheduleTimelineQuery()
  useEffect(() => {
    async function getTimeline() {
      if (
        debouncedInterval === undefined ||
        debouncedInterval < 0 ||
        !vehicleId
      ) {
        setMaintenanceTimeline(undefined)
        return
      }
      if (
        debouncedInterval > 240 &&
        intervalType === GetMaintenanceScheduleTimelineIntervalTypeEnum.Months
      ) {
        setMaintenanceTimeline(undefined)
        return
      }
      if (
        debouncedInterval > 300000 &&
        intervalType === GetMaintenanceScheduleTimelineIntervalTypeEnum.Miles
      ) {
        setMaintenanceTimeline(undefined)
        return
      }
      if (
        debouncedInterval > 500000 &&
        intervalType ===
          GetMaintenanceScheduleTimelineIntervalTypeEnum.Kilometers
      ) {
        setMaintenanceTimeline(undefined)
        return
      }
      const timeline = await getMaintenanceScheduleTimeline({
        authorization: accessToken,
        shop: shopId,
        vehicle: vehicleId,
        intervalType:
          intervalType as GetMaintenanceScheduleTimelineIntervalTypeEnum,
        intervalValue: debouncedInterval
      }).unwrap()
      setMaintenanceTimeline({
        timeline,
        intervalType:
          intervalType as GetMaintenanceScheduleTimelineIntervalTypeEnum
      })
    }
    getTimeline()
  }, [
    accessToken,
    getMaintenanceScheduleTimeline,
    debouncedInterval,
    intervalType,
    shopId,
    vehicleId
  ])

  return (
    <MaintenanceScheduleContext.Provider
      value={{
        maintenanceSchedules,
        selected,
        setSelected,
        maintenanceTimeline,
        intervalType,
        setIntervalType,
        interval,
        setInterval,
        severityType,
        setSeverityType,
        loadingSchedule,
        loadingTimeline,
        setMaintenanceTimeline
      }}>
      {children}
    </MaintenanceScheduleContext.Provider>
  )
}
